/** Load linear vesting Wallet objects owned by a genesis pool address. */

import { useSuiClient } from '@mysten/dapp-kit';
import { computeLinearClaimableMist } from '@cpc/tx';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
	getVestingPackageIdFromEnv,
	linearWalletType,
	resolveVestingPackageId,
} from '~/config/vestingConfig';

/** Prefer short `0x2::dgc::DGC` for StructType filters (matches Move type tags). */
const DGC_TYPE_FOR_STRUCT = '0x2::dgc::DGC';

export type PoolVestingWalletView = {
	objectId: string;
	remainingMist: bigint;
	claimedMist: bigint;
	totalMist: bigint;
	startMs: number;
	durationMs: number;
	claimableMist: bigint;
	progressPct: number;
	ended: boolean;
	notStarted: boolean;
};

function readBalanceValue(balanceField: unknown): bigint {
	if (balanceField == null) return 0n;
	if (typeof balanceField === 'string' || typeof balanceField === 'number') {
		return BigInt(balanceField);
	}
	if (typeof balanceField === 'object') {
		const o = balanceField as { fields?: { value?: string | number }; value?: string | number };
		const v = o.fields?.value ?? o.value;
		if (v != null) return BigInt(v);
	}
	return 0n;
}

function parseWalletFields(fields: Record<string, unknown>): Omit<
	PoolVestingWalletView,
	'objectId' | 'claimableMist' | 'progressPct' | 'ended' | 'notStarted'
> | null {
	const remainingMist = readBalanceValue(fields.balance);
	const claimedMist = BigInt(String(fields.claimed ?? 0));
	const startMs = Number(fields.start ?? 0);
	const durationMs = Number(fields.duration ?? 0);
	if (!Number.isFinite(startMs) || !Number.isFinite(durationMs) || durationMs <= 0) {
		return null;
	}
	return {
		remainingMist,
		claimedMist,
		totalMist: remainingMist + claimedMist,
		startMs,
		durationMs,
	};
}

function enrich(
	base: Omit<
		PoolVestingWalletView,
		'objectId' | 'claimableMist' | 'progressPct' | 'ended' | 'notStarted'
	> & { objectId: string },
	nowMs: number,
): PoolVestingWalletView {
	const claimableMist = computeLinearClaimableMist({
		remainingMist: base.remainingMist,
		claimedMist: base.claimedMist,
		startMs: base.startMs,
		durationMs: base.durationMs,
		nowMs,
	});
	const unlocked = base.claimedMist + claimableMist;
	const progressPct =
		base.totalMist > 0n
			? Math.min(100, Number((unlocked * 10000n) / base.totalMist) / 100)
			: 0;
	return {
		...base,
		claimableMist,
		progressPct,
		ended: nowMs >= base.startMs + base.durationMs,
		notStarted: nowMs < base.startMs,
	};
}

/** Parse package id from `0x…::linear::Wallet<…>` type tags. */
function packageIdFromLinearWalletType(typeTag: string | undefined | null): string | null {
	if (!typeTag) return null;
	const m = typeTag.match(/^(0x[0-9a-fA-F]+)::linear::Wallet</);
	return m?.[1] ?? null;
}

async function discoverVestingPackageFromOwner(
	client: ReturnType<typeof useSuiClient>,
	owner: string,
): Promise<{ packageId: string; wallets: PoolVestingWalletView[] } | null> {
	const pages: PoolVestingWalletView[] = [];
	let packageId: string | null = null;
	let cursor: string | null | undefined;
	const now = Date.now();
	do {
		const page = await client.getOwnedObjects({
			owner,
			cursor: cursor ?? undefined,
			limit: 50,
			options: { showContent: true, showType: true },
		});
		for (const item of page.data ?? []) {
			const typeTag = item.data?.type ?? (item.data?.content as { type?: string } | undefined)?.type;
			const pkg = packageIdFromLinearWalletType(typeTag);
			if (!pkg) continue;
			packageId = pkg;
			const id = item.data?.objectId;
			const content = item.data?.content;
			if (!id || !content || content.dataType !== 'moveObject') continue;
			const fields = content.fields as Record<string, unknown>;
			const parsed = parseWalletFields(fields);
			if (!parsed) continue;
			pages.push(enrich({ ...parsed, objectId: id }, now));
		}
		cursor = page.hasNextPage ? page.nextCursor : null;
	} while (cursor);
	if (!packageId) return null;
	return { packageId, wallets: pages };
}

export function usePoolVestingWallets(owner: string | undefined, enabled: boolean) {
	const client = useSuiClient();
	const [packageId, setPackageId] = useState<string | null>(() => getVestingPackageIdFromEnv());
	const [pkgLoading, setPkgLoading] = useState(() => !getVestingPackageIdFromEnv());

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const id = await resolveVestingPackageId();
			if (cancelled) return;
			setPackageId(id);
			setPkgLoading(false);
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const query = useQuery({
		queryKey: ['pool-vesting-wallets', owner, packageId ?? 'discover'],
		enabled: Boolean(enabled && owner && !pkgLoading),
		refetchInterval: 30_000,
		queryFn: async (): Promise<{ packageId: string | null; wallets: PoolVestingWalletView[] }> => {
			if (!owner) return { packageId: null, wallets: [] };
			const now = Date.now();

			if (packageId) {
				const structType = linearWalletType(packageId, DGC_TYPE_FOR_STRUCT);
				const pages: PoolVestingWalletView[] = [];
				let cursor: string | null | undefined;
				do {
					const page = await client.getOwnedObjects({
						owner,
						cursor: cursor ?? undefined,
						limit: 50,
						filter: { StructType: structType },
						options: { showContent: true, showType: true },
					});
					for (const item of page.data ?? []) {
						const id = item.data?.objectId;
						const content = item.data?.content;
						if (!id || !content || content.dataType !== 'moveObject') continue;
						const fields = content.fields as Record<string, unknown>;
						const parsed = parseWalletFields(fields);
						if (!parsed) continue;
						pages.push(enrich({ ...parsed, objectId: id }, now));
					}
					cursor = page.hasNextPage ? page.nextCursor : null;
				} while (cursor);
				if (pages.length > 0) return { packageId, wallets: pages };
				// Configured package but no wallets — fall through to discovery
			}

			const discovered = await discoverVestingPackageFromOwner(client, owner);
			if (discovered) return discovered;
			return { packageId: packageId ?? null, wallets: [] };
		},
	});

	const resolvedPackageId = query.data?.packageId ?? packageId;
	const wallets = query.data?.wallets ?? [];

	useEffect(() => {
		const discovered = query.data?.packageId;
		if (discovered && discovered !== packageId) {
			setPackageId(discovered);
		}
	}, [packageId, query.data?.packageId]);

	return {
		packageId: resolvedPackageId,
		pkgLoading,
		wallets,
		isPending: pkgLoading || query.isPending,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	};
}
